import { property, noop, any } from 'underscore';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ActuationContext } from './actuation-context';
import { consoleLog } from './actuator-id';
import { useActuatorsTracking } from './use-actuators-tracking';

const needsActivation = ({ phase, actuators }) => any(actuators, b => b.needsActivation(phase));

export const ActuationGroup = ({ children, actuationProps: actuationPropsFromProps }) => {
  const { isActive, debugMode: debugModeFromProps } = actuationPropsFromProps;
  // We currently support either a root-node-mode or a proxy mode
  // - in the root-node-mode the parentContext is undefined
  // - in the proxy mode the parentContext is defined, isActive can be used for external control
  //   of the proxy's sub-tree ignoring parent's isActive and focusing tracking
  const [phase, setPhase] = useState(undefined);
  const activate = useCallback(({ phase: newParentPhase }) => {
    setPhase(newParentPhase);
  }, []);

  const {
    actuatorId, debugMode, isIsolated, registerActuator, unregisterActuator, actuatorsRef,
  } = useActuatorsTracking({ activate, needsActivation, debugMode: debugModeFromProps });

  const log = useMemo(() => (debugMode ? consoleLog : noop), [debugMode]);

  const [prevIsActive, setPrevIsActive] = useState(undefined);

  if (isIsolated && prevIsActive !== isActive) {
    if (isActive) {
      // HACK: settling of new errors and lightning up of beacons can take several rerenders
      // depending on how it all integrated. Without a guaranteed synchronization of beacons
      // with the group we have to delay the activation using the timer
      setTimeout(() => setPhase('expand'), 100);
    } else {
      setPhase(undefined);
    }
    setPrevIsActive(isActive);
  }

  useEffect(() => {
    // In the Isolated mode the group cycles through activation phases.
    // In the proxy-mode the group follows parent's phases.
    if (phase === 'expand') {
      for (let i = 0; i < actuatorsRef.current.length; i += 1) {
        const actuator = actuatorsRef.current[i];
        if (actuator.needsActivation('expand', actuatorId)) {
          log(`[DWD] Expand actuator ${actuatorId}/${actuator.id}`);
          actuator.activate('expand');
        }
      }
      if (isIsolated) {
        // HACK: settling of the "needs activation" state for actuator  can take several rerenders
        // depending on how it all integrated. Without a guaranteed synchronization of actuators
        // with the group we have to delay the activation using the timer
        setTimeout(() => setPhase('focus'), 500);
      }
    } else if (phase === 'focus') {
      for (let i = 0; i < actuatorsRef.current.length; i += 1) {
        const beacon = actuatorsRef.current[i];
        if (beacon.needsActivation('focus', actuatorId)) {
          log(`[DWD] Focus on beacon ${actuatorId}/${beacon.id}`);
          beacon.activate('focus');
          break;
        }
      }
      if (isIsolated) {
        setPhase(undefined);
      }
    } else {
      actuatorsRef.current.forEach((beacon) => {
        beacon.activate(undefined);
      });
    }
  }, [phase, actuatorId, isIsolated, actuatorsRef, log]);

  const actuatorContextProps = useMemo(() => ({
    registerActuator,
    unregisterActuator,
    debugMode,
  }), [registerActuator, unregisterActuator, debugMode]);

  return (
    <ActuationContext.Provider value={actuatorContextProps}>
      {debugMode && `ActGrp ID: ${actuatorId}. Acts: ${actuatorsRef.current.map(property('id')).join(', ')}`}
      {children}
    </ActuationContext.Provider>
  );
};

ActuationGroup.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  actuationProps: PropTypes.shape({
    isActive: PropTypes.bool,
    debugMode: PropTypes.bool,
  }),
};

ActuationGroup.defaultProps = {
  actuationProps: {
    isActive: undefined,
    debugMode: false,
  },
};

export const withActuationGroupProxy = (WrappedComponent) => {
  const HOC = props => (
    <ActuationGroup>
      <WrappedComponent {...props} />
    </ActuationGroup>
  );

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withActuationGroupProxy(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
